import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Building, Settings, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const GeneralIndustry = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/general-industry"]?.title} description={seoData["industries/general-industry"]?.description} path="/industries/general-industry" keywords={seoData["industries/general-industry"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="General Industry"
        description="Full service industrial coating contractor for all industries. Turnkey protective coatings, corrosion repair, tank lining, floor coating, and infrastructure rehabilitation. Serving California, Arizona, Nevada, and Texas."
        backgroundKey="general-industry"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Corrosion on steel structures, tanks, and piping systems',
              'Floor deterioration from heavy traffic and chemical exposure',
              'Equipment wear and mechanical failure',
              'Concrete spalling and structural degradation',
              'Secondary containment failures',
              'Unplanned downtime from asset deterioration'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-gray-50 rounded-lg">
                <Building className="text-gray-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-gray-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Settings className="text-gray-600 mr-3 flex-shrink-0" size={22} />
                  Protective Coating Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Complete coating solutions for steel, concrete, and equipment — from surface preparation through final coating application. Epoxy, polyurethane, polyurea, and specialty coating systems matched to your specific environment and service conditions.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-gray-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Settings className="text-gray-600 mr-3 flex-shrink-0" size={22} />
                  Tank Coating & Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Interior and exterior tank coating for water storage, chemical storage, fuel storage, and process tanks. NSF 61 potable water systems, chemical-resistant linings, and abrasion-resistant coatings.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-gray-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Settings className="text-gray-600 mr-3 flex-shrink-0" size={22} />
                  Equipment Repair & Rebuild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">In-situ repair of pumps, valves, mixers, heat exchangers, and rotating equipment. Cold-applied epoxy composites restore equipment to OEM specifications without welding or machining.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-gray-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Settings className="text-gray-600 mr-3 flex-shrink-0" size={22} />
                  Infrastructure Rehabilitation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Concrete repair, pipe rehabilitation, floor coating, and structural steel protection. Extend the service life of your facility infrastructure and reduce total cost of ownership.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for General Industry?</h2>
              <div className="space-y-3">
                {['Turnkey contractor — assessment, surface prep, application, and QA/QC',
                  '25+ years experience across all industrial sectors',
                  'WBENC certified woman-owned business',
                  'Full range of coating systems for any environment',
                  'No hot work — cold-applied repair solutions',
                  '24/7 emergency response for critical failures'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gray-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Your Industrial Coating Partner</h3>
              <p className="text-gray-200 mb-6">BCS is a full service industrial coating contractor serving all industries. One contractor, one call, complete coating and repair solutions for your facility.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View All Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Get Started with BCS</h2>
          <p className="text-blue-100 mb-8">Contact us for a free facility assessment and coating recommendation. We serve all industrial sectors across California and the Southwest.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GeneralIndustry;
